import { Component, forwardRef, Input, OnDestroy } from "@angular/core";
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from "@angular/forms";
import { DropdownItems } from "@material-library";
import { Subject, takeUntil } from "rxjs";
import { UserService } from "src/app/core/user.service";
import { FileItemService } from "src/app/file/file-item.service";
import { LinkListService } from "src/app/links/services/link-list.service";
import { requiredIfValidator } from "src/app/shared/func";
import { BlendProperties, LinkListItem } from "src/app/shared/models";
import { IFile } from "src/sync";

type ItemType = 'file' | 'dir';
export interface LinkFormFields {
  passwordprotect: string;
  permission: BlendProperties;
  upload: boolean;
  exp_date: Date;
  exp_time: string;
  use_password: boolean;
  use_link_expiry: boolean;
}

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

type Field = keyof LinkFormFields;

export function mapFormToApiParams(
  permissionValue: BlendProperties,
  uploadsEnabled: boolean,
  itemType: string
): { previewonly: number; file_edit: number; upload: number } {
  let previewonly: number;
  let file_edit: number;
  let upload: number;

  if (itemType === 'file') {
    switch (permissionValue) {
      case BlendProperties.LINK_PERMISSION_VIEW:
        previewonly = 1;
        file_edit = 0;
        break;
      case BlendProperties.LINK_PERMISSION_DOWNLOAD:
        previewonly = 0;
        file_edit = 0;
        break;
      case BlendProperties.LINK_PERMISSION_EDITABLE:
        previewonly = 0;
        file_edit = 1;
        break;
      default:
        throw new Error('Invalid blend property for file');
    }

    // UploadsEnabled for files may be used to allow editing
    upload = uploadsEnabled ? 1 : 0;
    if (file_edit === 1 && upload === 0) {
      // Editing requires uploads to be enabled
      upload = 1;
    }
  } else if (itemType === 'dir') {
    switch (permissionValue) {
      case BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS:
        previewonly = 0;
        file_edit = 0;
        upload = uploadsEnabled ? 2 : 0; // Upload enabled, hide files or disabled
        break;
      case BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS:
        previewonly = 1;
        file_edit = 0;
        upload = uploadsEnabled ? 1 : 0; // Upload enabled, show files or disabled
        break;
      case BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS:
        previewonly = 0;
        file_edit = 0;
        upload = uploadsEnabled ? 1 : 0; // Upload enabled, show files or disabled
        break;
      case BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS:
        previewonly = 0;
        file_edit = 1;
        upload = uploadsEnabled ? 1 : 0; // Upload enabled, show files or disabled
        if (upload === 0) {
          throw new Error('Editing is not allowed when uploads are disabled');
        }
        break;
      default:
        throw new Error('Invalid blend property for directory');
    }
  } else {
    throw new Error('Invalid item type');
  }

  return {
    previewonly,
    file_edit,
    upload,
  };
}

function mapApiParamsToFormValues(
  apiParams: { previewonly: number; file_edit: number; upload: number },
  itemType: ItemType
): { permission: BlendProperties; uploadsEnabled: boolean } {
  let permission: BlendProperties;
  let uploadsEnabled: boolean;

  if (itemType === 'file') {
    uploadsEnabled = apiParams.upload === 1;

    if (apiParams.file_edit === 1) {
      permission = BlendProperties.LINK_PERMISSION_EDITABLE;
    } else if (apiParams.previewonly === 1) {
      permission = BlendProperties.LINK_PERMISSION_VIEW;
    } else if (apiParams.previewonly === 0) {
      permission = BlendProperties.LINK_PERMISSION_DOWNLOAD;
    } else {
      throw new Error('Invalid API parameters for file');
    }
  } else if (itemType === 'dir') {
    uploadsEnabled = apiParams.upload > 0;

    if (apiParams.upload === 2) {
      permission = BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS;
    } else if (apiParams.file_edit === 1) {
      permission = BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS;
    } else if (apiParams.previewonly === 1) {
      permission = BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS;
    } else if (apiParams.previewonly === 0) {
      permission = BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS;
    } else {
      throw new Error('Invalid API parameters for directory');
    }
  } else {
    console.log(itemType);
    throw new Error('Invalid item type');
  }

  return {
    permission,
    uploadsEnabled,
  };
}

const permissionMapping = {
  [BlendProperties.LINK_PERMISSION_VIEW]: 'View only',
  [BlendProperties.LINK_PERMISSION_DOWNLOAD]: 'View and download',
  [BlendProperties.LINK_PERMISSION_EDITABLE]: 'View, download and edit',
};

const folderPermissionMapping = {
  [BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS]: 'Hide folder contents',
  [BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS]: 'View folder contents',
  [BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS]: 'View and download folder contents',
  [BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS]: 'View, download, and edit folder contents',
};

@Component({
  selector: 'app-permissions-link-form',
  templateUrl: './link-permissions-form.component.html',
  styleUrls: ['./link-permissions-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LinkPermissionsFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LinkPermissionsFormComponent),
      multi: true
    }
  ]
})
export class LinkPermissionsFormComponent implements ControlValueAccessor, Validators, OnDestroy {
  @Input() fileType: string;
  @Input() link?: any;
  @Input() item: LinkListItem | IFile;

  protected form: FormGroup<FormControls<LinkFormFields>>;
  protected isPro = 0;
  protected isMSOfficeFile = false;

  protected linkEnforcedSettings = {
    isLinkExpiryDateRequired: false,
    linkExpiryDateDefault: null,
    isLinkPasswordRequired: false,
    linkPasswordDefault: null
  };

  protected expDateMin = new Date();
  protected permissionList: DropdownItems[] = [];
  private destroySubscriptions$ = new Subject<void>();

  constructor(
    private linklistService: LinkListService,
    private fb: FormBuilder,
    private userService: UserService,
    private fileItemService: FileItemService,
  ) { }

  ngOnInit() {
    this.linkEnforcedSettings = this.linklistService.getLinkEnforcedSettings();
    let expDate: Date, expTime: string;

    let date: Date;
    if (this.link.exp_servtime) {
      date = new Date(this.link.exp_servtime);
    }
    else if (this.linkEnforcedSettings.linkExpiryDateDefault) {
      date = new Date();
      date.setDate(date.getDate() + this.linkEnforcedSettings.linkExpiryDateDefault);
    }

    if (date) {
      expDate = date;
      expTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }

    const formValues = mapApiParamsToFormValues({
      previewonly: this.link.previewonly,
      file_edit: this.link.file_edit,
      upload: this.link.upload,
    }, this.link.type);

    this.isPro = this.userService.get('is_pro');
    this.isMSOfficeFile = this.fileItemService.isMSOffice(this.item as IFile, 'edit');

    this.setPermissionsBasedOnFileType();

    const permissionIndex = this.permissionList.findIndex(p => p.value === formValues.permission);
    if (this.permissionList[permissionIndex]?.disabled) {
      formValues.permission = this.permissionList[permissionIndex - 1]?.value as BlendProperties;
    }

    this.form = this.fb.group({
      passwordprotect: [
        this.link.passwordprotect,
        [
          requiredIfValidator(() => this.form.controls.use_password.value),
          Validators.maxLength(128) 
        ]
      ],
      upload: [formValues.uploadsEnabled, Validators.required],
      permission: [
        formValues.permission,
        Validators.required,
      ],
      exp_date: [
        expDate,
        requiredIfValidator(() => this.form.controls.use_link_expiry.value),
      ],
      exp_time: [
        expTime,
        Validators.compose([
          requiredIfValidator(() => this.form.controls.use_link_expiry.value),
          control => {
            if (this.form?.controls?.use_link_expiry?.value) {
              const now = new Date();
              const expDate = this.form?.controls?.exp_date?.value;
              if (expDate) {
                if (expDate.getFullYear() < now.getFullYear() || (expDate.getFullYear() == now.getFullYear() && expDate.getMonth() < now.getMonth()) || (expDate.getFullYear() == now.getFullYear() && expDate.getMonth() == now.getMonth() && expDate.getDate() < now.getDate())) {
                  return { invalidTime: true };
                }
                else if (expDate.getFullYear() == now.getFullYear() && expDate.getMonth() == now.getMonth() && expDate.getDate() == now.getDate()) {
                  const [hours, minutes] = control.value.split(':');
                  if (parseInt(hours, 10) < now.getHours() || (parseInt(hours, 10) == now.getHours() && parseInt(minutes, 10) <= now.getMinutes())) {
                    return { invalidTime: true };
                  }
                }
              }
            }
            return null;
          }
        ]),
      ],
      use_password: [
        !!(this.linkEnforcedSettings.isLinkPasswordRequired || this.link.password),
      ],

      use_link_expiry: [
        !!(this.linkEnforcedSettings.isLinkExpiryDateRequired || this.link.exp_servtime),
      ],
    });

    this.form.valueChanges
    .pipe(takeUntil(this.destroySubscriptions$))
    .subscribe(value => {
      if (this.onChange) {
        this.onChange(value);
      }
    });
  
    this.form.controls.use_password.valueChanges
    .pipe(takeUntil(this.destroySubscriptions$))
    .subscribe(() => {
      this.form.controls.passwordprotect.updateValueAndValidity();
    });

    this.form.controls.use_link_expiry.valueChanges
    .pipe(takeUntil(this.destroySubscriptions$))
    .subscribe(() => {
      this.form.controls.exp_date.updateValueAndValidity();
      this.form.controls.exp_time.updateValueAndValidity();
    });

    this.form.controls.permission.valueChanges
    .pipe(takeUntil(this.destroySubscriptions$))
    .subscribe(value => {
      if (value === BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS
        || value === BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS) {
        this.form.controls.upload.setValue(true);
      }
    });

    this.form.controls.upload.valueChanges
    .pipe(takeUntil(this.destroySubscriptions$))
    .subscribe(value => {
      if (!value) {
        if (this.form.controls.permission.value === BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS) {
          this.form.controls.permission.setValue(BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS);
        }
        else if (this.form.controls.permission.value === BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS) {
          this.form.controls.permission.setValue(BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS);
        }
      }
    });



    

    if (this.onChange) {
      this.onChange(this.form.value);
    }
  }

  ngAfterViewInit() {
    if (this.onChange && this.form) {
      this.onChange(this.form.value);
    }
  }

  ngOnDestroy(): void {
    this.destroySubscriptions$.next();
    this.destroySubscriptions$.complete();
  }
  
  private setPermissionsBasedOnFileType() {
    if (this.fileType === 'dir') {
      this.permissionList = [
        { value: BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS, name: folderPermissionMapping[BlendProperties.LINK_PERMISSION_HIDE_FOLDER_CONTENTS] },
        { value: BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS, name: folderPermissionMapping[BlendProperties.LINK_PERMISSION_VIEW_FOLDER_CONTENTS] },
        { value: BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS, name: folderPermissionMapping[BlendProperties.LINK_PERMISSION_DOWNLOAD_FOLDER_CONTENTS] },
        { value: BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS, name: folderPermissionMapping[BlendProperties.LINK_PERMISSION_EDITABLE_FOLDER_CONTENTS], 
          ...(this.item.is_shared ? { subtext: 'Restricted for files in shared folders to maintain data integrity', disabled: true } : {}) },
      ];  
    } else {
      this.permissionList = [
        { value: BlendProperties.LINK_PERMISSION_VIEW, name: permissionMapping[BlendProperties.LINK_PERMISSION_VIEW] },
        { value: BlendProperties.LINK_PERMISSION_DOWNLOAD, name: permissionMapping[BlendProperties.LINK_PERMISSION_DOWNLOAD] },
        this.isMSOfficeFile && { value: BlendProperties.LINK_PERMISSION_EDITABLE, name: permissionMapping[BlendProperties.LINK_PERMISSION_EDITABLE], 
          ...(this.item.is_shared ? { subtext: 'Restricted for files in shared folders to maintain data integrity', disabled: true } : {}) },
      ].filter(Boolean);
    }
  }

  protected getErrorMessage(controlName: Field) {
    if (!this.form.controls[controlName].touched) return;

    switch (controlName) {
      case 'passwordprotect':
        if (this.form.controls.passwordprotect.hasError('required')) {
          return 'Password is required';
        } else if (this.form.controls.passwordprotect.hasError('maxlength')) {
          return 'Password cannot exceed 128 characters';
        }
      break;

      case 'exp_date':
        return this.form.controls.exp_date.hasError('required') && 'Expiry date is required';

      case 'exp_time':
        if (this.form.controls.exp_time.hasError('invalidTime')) {
          return 'Expiry time must be in the future';
        }
        else if (this.form.controls.exp_time.hasError('required')) {
          return 'Expiry time is required';
        }
    }    
  }

  protected get shouldShowPassword() {
    return this.form.get('use_password')?.value ?? false;
  }

  protected get shouldShouldLinkExpiry() {
    return this.form.get('use_link_expiry')?.value ?? false;
  }

  protected get isEditPermission() {
    return this.form.controls.permission.value === BlendProperties.LINK_PERMISSION_EDITABLE;
  }

  protected onExpDateChange = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() + 1);
    this.form.controls.exp_time.setValue(`${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`);
    this.form.controls.exp_time.updateValueAndValidity();
  }

  // Validators methods
  validate(control: AbstractControl): ValidationErrors {
    return this.form.valid ? null : { invalidForm: { valid: false, errors: this.form.errors } };
  }
  registerOnValidatorChange?(fn: () => void): void { }

  // ControlValueAccessor methods
  private onChange: (value: any) => void = () => {};

  writeValue(value: any): void {
    if (value) {
      this.form.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {

  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}